import React, { useState, useEffect } from 'react';
import authService from '../../services/authService';
import './Admin.css';

const ALLOCATION_TYPE_TEAM = 'team';
const ALLOCATION_TYPE_USER = 'user';

const CreditAllocationModal = ({ 
  organizationId, 
  teamId, 
  sourceAccountId, 
  availableBalance,
  teams = [],
  users = [],
  onSuccess, 
  onClose 
}) => {
  const [allocationType, setAllocationType] = useState(ALLOCATION_TYPE_TEAM);
  const [selectedTeamId, setSelectedTeamId] = useState(teamId || '');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [amount, setAmount] = useState('');
  const [expirationDays, setExpirationDays] = useState('');
  const [allocationTypeOption, setAllocationTypeOption] = useState('BUDGET'); // BUDGET, GRANT, TEMPORARY
  const [notes, setNotes] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleAllocate = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    const amountValue = parseFloat(amount);
    if (amountValue > availableBalance) {
      setError(`Insufficient balance. Available: $${availableBalance.toFixed(2)}`);
      return;
    }

    if (allocationType === ALLOCATION_TYPE_TEAM && !selectedTeamId) {
      setError('Please select a team');
      return;
    }

    if (allocationType === ALLOCATION_TYPE_USER && !selectedUserId) {
      setError('Please select a user');
      return;
    }

    setShowPreview(true);
  };

  const handleConfirm = async () => {
    setProcessing(true);
    setError('');

    try {
      const t = authService.getToken();
      const amountValue = parseFloat(amount);

      let endpoint, body;
      
      if (allocationType === ALLOCATION_TYPE_TEAM) {
        endpoint = '/api/credits/allocate/team';
        body = {
          organizationId: organizationId,
          teamId: parseInt(selectedTeamId),
          amount: amountValue,
          expirationDays: expirationDays ? parseInt(expirationDays) : null,
          allocationType: allocationTypeOption,
          autoReclaim: allocationTypeOption === 'TEMPORARY',
          notes: notes || null
        };
      } else {
        endpoint = '/api/credits/allocate/user';
        body = {
          sourceAccountId: sourceAccountId,
          userId: selectedUserId,
          amount: amountValue,
          expirationDays: expirationDays ? parseInt(expirationDays) : null,
          allocationType: allocationTypeOption,
          autoReclaim: allocationTypeOption === 'TEMPORARY',
          notes: notes || null
        };
      }

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(t ? { Authorization: `Bearer ${t}` } : {})
        },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ message: 'Failed to allocate credits' }));
        throw new Error(errorData.message || 'Failed to allocate credits');
      }

      const data = await res.json();
      
      // Trigger balance refresh
      try {
        localStorage.setItem('balanceRefreshTick', String(Date.now()));
        setTimeout(() => {
          try {
            localStorage.setItem('balanceRefreshTick', String(Date.now()));
          } catch {}
        }, 500);
      } catch {}

      if (onSuccess) {
        onSuccess(data);
      }
      
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to allocate credits');
      setShowPreview(false);
    } finally {
      setProcessing(false);
    }
  };

  const handleBack = () => {
    setShowPreview(false);
    setError('');
  };

  const selectedTeam = teams.find(t => t.id === parseInt(selectedTeamId) || t.Id === parseInt(selectedTeamId));
  const selectedUser = users.find(u => u.user_id === selectedUserId || u.userId === selectedUserId || u.id === selectedUserId);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '90%' }}>
        <div className="modal-header">
          <h2>Allocate Credits</h2>
          {!processing && (
            <button className="modal-close" onClick={onClose}>×</button>
          )}
        </div>

        <div className="modal-body">
          {error && (
            <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          {!showPreview ? (
            <>
              {/* Allocation Type Selection */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Allocate To
                </label>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      checked={allocationType === ALLOCATION_TYPE_TEAM}
                      onChange={() => {
                        setAllocationType(ALLOCATION_TYPE_TEAM);
                        setSelectedUserId('');
                        setError('');
                      }}
                    />
                    <span>Team</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      checked={allocationType === ALLOCATION_TYPE_USER}
                      onChange={() => {
                        setAllocationType(ALLOCATION_TYPE_USER);
                        setSelectedTeamId('');
                        setError('');
                      }}
                    />
                    <span>User</span>
                  </label>
                </div>
              </div>

              {/* Available Balance */}
              <div className="dashboard-card" style={{ padding: '1rem', marginBottom: '1.5rem', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: '500' }}>Available Balance:</span>
                  <strong style={{ fontSize: '1.25rem', color: '#0369a1' }}>${availableBalance.toFixed(2)}</strong>
                </div>
              </div>

              {/* Team Selection */}
              {allocationType === ALLOCATION_TYPE_TEAM && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Select Team
                  </label>
                  <select
                    className="form-control"
                    value={selectedTeamId}
                    onChange={(e) => {
                      setSelectedTeamId(e.target.value);
                      setError('');
                    }}
                  >
                    <option value="">-- Select Team --</option>
                    {teams.map(team => (
                      <option key={team.id || team.Id} value={team.id || team.Id}>
                        {team.name || team.Name} {team.id || team.Id === teamId ? '(Current)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* User Selection */}
              {allocationType === ALLOCATION_TYPE_USER && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Select User
                  </label>
                  <select
                    className="form-control"
                    value={selectedUserId}
                    onChange={(e) => {
                      setSelectedUserId(e.target.value);
                      setError('');
                    }}
                  >
                    <option value="">-- Select User --</option>
                    {users.map(user => (
                      <option key={user.user_id || user.userId || user.id} value={user.user_id || user.userId || user.id}>
                        {user.email || user.Email || user.name || user.Name || user.user_id || user.userId || user.id}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Amount */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Amount ($)
                </label>
                <input
                  type="number"
                  className="form-control"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setError('');
                  }}
                  placeholder="Enter amount"
                  min="0.01"
                  max={availableBalance}
                  step="0.01"
                />
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  Maximum: ${availableBalance.toFixed(2)}
                </div>
              </div>

              {/* Allocation Type Option */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Allocation Type
                </label>
                <select
                  className="form-control"
                  value={allocationTypeOption}
                  onChange={(e) => setAllocationTypeOption(e.target.value)}
                >
                  <option value="BUDGET">Budget (Standard allocation)</option>
                  <option value="GRANT">Grant (One-time grant)</option>
                  <option value="TEMPORARY">Temporary (Auto-reclaim unused)</option>
                </select>
              </div>

              {/* Expiration Days (Optional) */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Expiration Days (Optional)
                </label>
                <input
                  type="number"
                  className="form-control"
                  value={expirationDays}
                  onChange={(e) => setExpirationDays(e.target.value)}
                  placeholder="Leave empty for no expiration"
                  min="1"
                />
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  Credits will expire after this many days. Leave empty for no expiration.
                </div>
              </div>

              {/* Notes (Optional) */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Notes (Optional)
                </label>
                <textarea
                  className="form-control"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes about this allocation"
                  rows="3"
                />
              </div>
            </>
          ) : (
            <>
              {/* Preview */}
              <div>
                <h3 style={{ marginBottom: '1rem' }}>Review Allocation</h3>
                <div className="dashboard-card" style={{ padding: '1rem' }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Allocation Summary</strong>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span>Allocate To:</span>
                    <strong>
                      {allocationType === ALLOCATION_TYPE_TEAM 
                        ? `Team: ${selectedTeam?.name || selectedTeam?.Name || selectedTeamId}`
                        : `User: ${selectedUser?.email || selectedUser?.Email || selectedUser?.name || selectedUser?.Name || selectedUserId}`
                      }
                    </strong>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span>Amount:</span>
                    <strong>${parseFloat(amount || 0).toFixed(2)}</strong>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span>Allocation Type:</span>
                    <span>{allocationTypeOption}</span>
                  </div>
                  
                  {expirationDays && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span>Expires In:</span>
                      <span>{expirationDays} days</span>
                    </div>
                  )}
                  
                  {notes && (
                    <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #ddd' }}>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Notes:</div>
                      <div style={{ fontSize: '0.875rem' }}>{notes}</div>
                    </div>
                  )}
                  
                  <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#f0f9ff', borderRadius: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                      <span>Available After:</span>
                      <strong>${(availableBalance - parseFloat(amount || 0)).toFixed(2)}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
          {showPreview && (
            <button className="btn btn-secondary" onClick={handleBack} disabled={processing}>
              Back
            </button>
          )}
          <div style={{ marginLeft: 'auto' }}>
            {!showPreview ? (
              <button
                className="btn btn-primary"
                onClick={handleAllocate}
                disabled={processing || !amount || parseFloat(amount) <= 0}
              >
                Continue
              </button>
            ) : (
              <button
                className="btn btn-primary"
                onClick={handleConfirm}
                disabled={processing}
              >
                {processing ? 'Allocating...' : 'Confirm Allocation'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditAllocationModal;

